// Generic postal code → region mapping for all supported marketplaces
// US: 3-digit zip prefix → state, CA: first letter → province, UK: postcode area → region

import type { MarketplaceCode } from '../types/transaction';
import { zipToState, STATE_NAMES } from './zipToState';

// ============================================
// CA — First letter → Province/Territory
// ============================================
const CA_POSTAL_TO_PROVINCE: Record<string, string> = {
  'A': 'NL', 'B': 'NS', 'C': 'PE', 'E': 'NB',
  'G': 'QC', 'H': 'QC', 'J': 'QC',
  'K': 'ON', 'L': 'ON', 'M': 'ON', 'N': 'ON', 'P': 'ON',
  'R': 'MB', 'S': 'SK', 'T': 'AB', 'V': 'BC',
  'X': 'NT', 'Y': 'YT',
};

const CA_PROVINCE_NAMES: Record<string, string> = {
  'AB': 'Alberta', 'BC': 'British Columbia', 'MB': 'Manitoba',
  'NB': 'New Brunswick', 'NL': 'Newfoundland and Labrador',
  'NS': 'Nova Scotia', 'NT': 'Northwest Territories', 'NU': 'Nunavut',
  'ON': 'Ontario', 'PE': 'Prince Edward Island', 'QC': 'Québec',
  'SK': 'Saskatchewan', 'YT': 'Yukon',
};

// ============================================
// UK — Postcode area (1-2 letters) → 12 Regions
// ============================================
const UK_POSTCODE_TO_REGION: Record<string, string> = {
  // Scotland
  'AB': 'SCT', 'DD': 'SCT', 'DG': 'SCT', 'EH': 'SCT', 'FK': 'SCT',
  'G': 'SCT', 'HS': 'SCT', 'IV': 'SCT', 'KA': 'SCT', 'KW': 'SCT',
  'KY': 'SCT', 'ML': 'SCT', 'PA': 'SCT', 'PH': 'SCT', 'TD': 'SCT', 'ZE': 'SCT',
  // Wales
  'CF': 'WLS', 'LD': 'WLS', 'LL': 'WLS', 'NP': 'WLS', 'SA': 'WLS', 'SY': 'WLS',
  // Northern Ireland
  'BT': 'NIR',
  // North East
  'DH': 'NE', 'DL': 'NE', 'NE': 'NE', 'SR': 'NE', 'TS': 'NE',
  // North West
  'BB': 'NW', 'BL': 'NW', 'CA': 'NW', 'CH': 'NW', 'CW': 'NW',
  'FY': 'NW', 'L': 'NW', 'LA': 'NW', 'M': 'NW', 'OL': 'NW',
  'PR': 'NW', 'SK': 'NW', 'WA': 'NW', 'WN': 'NW',
  // Yorkshire and the Humber
  'BD': 'YH', 'DN': 'YH', 'HD': 'YH', 'HG': 'YH', 'HU': 'YH',
  'HX': 'YH', 'LS': 'YH', 'S': 'YH', 'WF': 'YH', 'YO': 'YH',
  // East Midlands
  'DE': 'EM', 'LE': 'EM', 'LN': 'EM', 'NG': 'EM', 'NN': 'EM',
  // West Midlands
  'B': 'WM', 'CV': 'WM', 'DY': 'WM', 'HR': 'WM', 'ST': 'WM',
  'TF': 'WM', 'WR': 'WM', 'WS': 'WM', 'WV': 'WM',
  // East of England
  'AL': 'EE', 'CB': 'EE', 'CM': 'EE', 'CO': 'EE', 'EN': 'EE',
  'HA': 'EE', 'HP': 'EE', 'IP': 'EE', 'LU': 'EE', 'NR': 'EE',
  'PE': 'EE', 'SG': 'EE', 'SS': 'EE', 'WD': 'EE',
  // Greater London
  'E': 'LDN', 'EC': 'LDN', 'N': 'LDN', 'NW': 'LDN',
  'SE': 'LDN', 'SW': 'LDN', 'W': 'LDN', 'WC': 'LDN',
  'BR': 'LDN', 'CR': 'LDN', 'DA': 'LDN', 'IG': 'LDN',
  'KT': 'LDN', 'RM': 'LDN', 'SM': 'LDN', 'TW': 'LDN', 'UB': 'LDN',
  // South East
  'BN': 'SE', 'CT': 'SE', 'GU': 'SE', 'ME': 'SE', 'MK': 'SE',
  'OX': 'SE', 'PO': 'SE', 'RG': 'SE', 'RH': 'SE', 'SL': 'SE',
  'SO': 'SE', 'TN': 'SE',
  // South West
  'BA': 'SW', 'BH': 'SW', 'BS': 'SW', 'DT': 'SW', 'EX': 'SW',
  'GL': 'SW', 'PL': 'SW', 'SN': 'SW', 'SP': 'SW', 'TA': 'SW',
  'TQ': 'SW', 'TR': 'SW',
};

const UK_REGION_NAMES: Record<string, string> = {
  'SCT': 'Scotland', 'WLS': 'Wales', 'NIR': 'N. Ireland',
  'NE': 'North East', 'NW': 'North West', 'YH': 'Yorkshire',
  'EM': 'East Midlands', 'WM': 'West Midlands', 'EE': 'East of England',
  'LDN': 'London', 'SE': 'South East', 'SW': 'South West',
};

/**
 * Extract postcode area from UK postal code.
 * e.g. "SW1A 1AA" → "SW", "M1 1AA" → "M", "LE67 1FB" → "LE"
 */
const extractUKPostcodeArea = (postal: string): string | null => {
  const clean = postal.trim().toUpperCase().split(' ')[0];
  if (!clean) return null;
  // Extract leading letters (1 or 2)
  const match = clean.match(/^([A-Z]{1,2})/);
  return match ? match[1] : null;
};

/**
 * Map a UK postcode area to a region code.
 * Tries 2-letter match first, then 1-letter fallback.
 */
const ukPostalToRegion = (postal: string): string | null => {
  const area = extractUKPostcodeArea(postal);
  if (!area) return null;
  // Try 2-letter first (more specific), then 1-letter
  return UK_POSTCODE_TO_REGION[area] || UK_POSTCODE_TO_REGION[area.charAt(0)] || null;
};

/**
 * Map a CA postal code to province.
 * e.g. "M4C 5L7" → "ON", "T6W 3R5" → "AB"
 */
const caPostalToProvince = (postal: string): string | null => {
  const first = postal.trim().toUpperCase().charAt(0);
  if (!first || !/[A-Z]/.test(first)) return null;
  return CA_POSTAL_TO_PROVINCE[first] || null;
};

// ============================================
// SUPPORTED MARKETPLACES for region mapping
// ============================================
export const REGION_MAP_SUPPORTED: MarketplaceCode[] = ['US', 'CA', 'UK'];

/**
 * Generic postal code → region code mapping.
 * Returns null for unsupported marketplaces or invalid postal codes.
 */
export const postalToRegion = (postal: string, marketplace: MarketplaceCode): string | null => {
  if (!postal) return null;
  switch (marketplace) {
    case 'US': return zipToState(postal);
    case 'CA': return caPostalToProvince(postal);
    case 'UK': return ukPostalToRegion(postal);
    default: return null;
  }
};

/**
 * Get region display name for a marketplace + region code.
 */
export const getRegionName = (regionCode: string, marketplace: MarketplaceCode): string => {
  switch (marketplace) {
    case 'US': return STATE_NAMES[regionCode] || regionCode;
    case 'CA': return CA_PROVINCE_NAMES[regionCode] || regionCode;
    case 'UK': return UK_REGION_NAMES[regionCode] || regionCode;
    default: return regionCode;
  }
};

/**
 * Get all region names for a marketplace.
 */
export const getAllRegionNames = (marketplace: MarketplaceCode): Record<string, string> => {
  switch (marketplace) {
    case 'US': return STATE_NAMES;
    case 'CA': return CA_PROVINCE_NAMES;
    case 'UK': return UK_REGION_NAMES;
    default: return {};
  }
};

/**
 * Get region title label for a marketplace.
 */
export const getRegionLabel = (marketplace: MarketplaceCode): { singular: string; plural: string; title: string } => {
  switch (marketplace) {
    case 'US': return { singular: 'State', plural: 'States', title: 'Top States' };
    case 'CA': return { singular: 'Province', plural: 'Provinces', title: 'Top Provinces' };
    case 'UK': return { singular: 'Region', plural: 'Regions', title: 'Top Regions' };
    default: return { singular: 'Region', plural: 'Regions', title: 'Top Regions' };
  }
};
